import { useI18n } from 'next-localization';
import useHelpers from 'hooks/helpers';
import DisplayingRange from 'components/includes/DisplayingRange';
import AbrdnSelect from 'components/Generic/Forms/FormFields/AbrdnSelect';
import ArticleCard, { ArticleFields } from 'components/Tiles/ArticleCard';
import Pagination from 'components/includes/Pagination';
import { useMemo } from 'react';
import styles from '@/components/Tiles/ArticleCard.module.scss';
import { ComponentProps } from 'lib/component-props';

type ArticleCardsComponentProps = ComponentProps & {
  articleCards: ArticleFields[];
  totalItems: number;
  pageNumber: number;
  pageSize: number;
  onPageNumberChange: (pageNumber: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  isLoading: boolean;
};

const PAGE_SIZE_OPTIONS = [20, 40, 60, 80, 100];

const ArticleCardsComponent = ({
  articleCards,
  totalItems,
  pageNumber,
  pageSize,
  onPageNumberChange,
  onPageSizeChange,
  rendering,
  isLoading,
}: ArticleCardsComponentProps): JSX.Element => {
  const { t } = useI18n();
  const { anchorId } = useHelpers(rendering);

  // Page size options for dropdown
  const pageSizeSelectOptions = useMemo(() => {
    return PAGE_SIZE_OPTIONS.map((size) => ({
      value: size,
      label: String(size),
      selected: pageSize === size,
    }));
  }, [pageSize]);

  const handlePageSizeChange = ({ value }: { value: number }) => {
    onPageSizeChange(value);
  };

  const maxPages = Math.ceil(totalItems / pageSize);

  return (
    <section id={anchorId} data-e2e-component-uid={rendering?.uid} className="container pb-5">
      {/* Top controls */}
      <div className="d-flex flex-column-reverse flex-sm-row align-items-sm-center mb-5 w-100 justify-content-between">
        <DisplayingRange
          className="footnote text-grey"
          page={pageNumber}
          dictionaryKey="paginationDisplayingInsightsResults"
          take={pageSize}
          total={totalItems}
        />

        <div className="d-flex align-items-center ms-auto ms-sm-0 mb-2 mb-sm-0">
          <p className="form-label me-2">{t('paginationShow')}</p>
          <AbrdnSelect
            key={pageSize}
            options={pageSizeSelectOptions}
            onChange={handlePageSizeChange}
            defaultValue={pageSizeSelectOptions.find((option) => option.selected)}
          />
        </div>
      </div>

      {/* Cards grid */}
      <div className={`row d-flex flex-wrap ${articleCards.length < 4 ? 'w-100' : ''}`}>
        {articleCards.map((article) => (
          <div className="col-12 col-md-6 col-lg-3 mb-3" key={article.id}>
            <ArticleCard fields={article} />
          </div>
        ))}
      </div>

      {/* Bottom pagination */}
      {totalItems > 0 && (
        <div
          className={`d-flex align-items-lg-center mt-2 mt-md-3 mt-xl-5 mb-0 mb-md-1 mb-xl-3 w-100 ${styles.relative}`}
        >
          <DisplayingRange
            className="footnote text-grey"
            dictionaryKey="paginationDisplayingInsightsResults"
            page={pageNumber}
            take={pageSize}
            total={totalItems}
          />

          <Pagination
            currentPage={pageNumber}
            maxPages={maxPages}
            className={`${styles.pagination} justify-content-center`}
            onSelect={onPageNumberChange}
            isLoading={isLoading}
          />
        </div>
      )}
    </section>
  );
};

export default ArticleCardsComponent;
